import React, { useState, useEffect } from "react";
import {
    Box,
    Dialog,
    DialogContent,
    Checkbox,
    ListItem,
    ListItemText,
    Tooltip
} from "@material-ui/core";
import CheckIcon from '@material-ui/icons/Check';
import COLORS from "scss/colors.module.scss";
import chroma from "chroma-js";
import styled from "styled-components";
import {
    ColumnName,
    SavingSpinnerForColumnEditors,
    ActionButtonsForColumnEditors,
    handleClose,
    ErrorMessageViewer,
    checkValuesUpdation
} from "./ColumnEditorFactory";
import { validateDropdownInputsBeforeSaving } from "../RowId/Validations";

const CheckboxGroupReusableEditor = ({
    initialValue = "",
    open,
    data,
    column,
    columnName,
    onSave,
    onCancel,
}) => {
    const [options, setOptions] = useState([]);
    const [selectedValues, setSelectedValues] = useState([]);
    const [validationError, setValidationError] = useState({ isInvalid: false, message: "" });
    const [isSaving, setIsSaving] = useState(false);

    const columnOptions = column?.normalizedColumnOptions ?? {};

    useEffect(() => {
        if (open) {
            const opts = (columnOptions.options || []).map((opt, idx) => ({
                value: opt.trim(),
                label: opt.trim(),
                color: columnOptions.colors?.[idx] || "#808080",
            }));
            setOptions(opts);

            const initVals = initialValue
                ? initialValue.split(",").map((v) => v.trim())
                : [];
            setSelectedValues(initVals);
        }
        return () => {
            setValidationError({ isInvalid: false, message: "" });
        }
    }, [open, initialValue]);

    const handleToggle = (value) => {
        setSelectedValues((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    const getCheckedValuesInOrder = () => {
        if (checkValuesUpdation(initialValue, selectedValues)) {
            return selectedValues.join(", ");
        } else {
            return initialValue;
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (!checkValuesUpdation(initialValue, selectedValues)) {
                onCancel();
                return;
            }
            const value = selectedValues.join(", ");
            const isUpdateAllowed = validateDropdownInputsBeforeSaving(value, column, data);
            if (isUpdateAllowed.isInvalid) {
                setValidationError(isUpdateAllowed);
                return;
            }
            await onSave(value, initialValue, column);
        } catch (err) {
            console.error("Error saving checkbox group value:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const getLabelColorStyle = (color = "#808080") => ({
        background: color,
        color: chroma.contrast(color, COLORS.PRIMARY_COLOR) > 2
        ? COLORS.PRIMARY_COLOR
        : COLORS.SECONDARY_COLOR,
        padding: "5px 12px",
        borderRadius: "20px",
        fontSize: "0.9rem",
    });

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(getCheckedValuesInOrder(), initialValue, onCancel, validationError, setValidationError)}
            fullWidth
            PaperProps={{
                style: {
                    width: validationError.isInvalid ? "450px" : "420px",
                    maxHeight: "420px",
                    borderRadius: "8px",
                },
            }}
        >
            <DialogContent>
                <Box display="flex" flexDirection="column" style={{ width: "100%", gap: 8 }}>
                    {columnName && <ColumnName columnName={columnName} />}

                    <StyledOptionsDiv>
                        {options.map((opt, idx) => (
                            <StyledListItem
                                key={idx}
                                button
                                onClick={() => handleToggle(opt.value)}
                            >
                                <Checkbox
                                    edge="start"
                                    checked={selectedValues.includes(opt.value)}
                                    tabIndex={-1}
                                    disableRipple
                                    style={{ color: opt.color }}
                                    icon={
                                        <span
                                            style={{
                                                display: "inline-block",
                                                width: 18,
                                                height: 18,
                                                borderRadius: 3,
                                                border: `2px solid ${opt.color}`,
                                            }}
                                        />
                                    }
                                    checkedIcon={
                                        <span
                                            style={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: 4,
                                                backgroundColor: opt.color,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                            >
                                            <CheckIcon style={{ fontSize: 16, color: "#fff" }} />
                                        </span>
                                    }
                                    sx={{ padding: 0, marginRight: 1 }}
                                />
                                <ListItemText
                                    primary={
                                        <Tooltip 
                                            title={opt?.label.length > 35 ? opt?.label : ""} 
                                            placement="bottom"
                                            PopperProps={{
                                                className: 'rowDetailTooltip',
                                            }}
                                        >
                                            <span style={getLabelColorStyle(opt.color)}>{opt?.label.length > 35 ? opt?.label.substring(0, 35) + "..." : opt?.label}</span>
                                        </Tooltip>
                                    }
                                />
                            </StyledListItem>
                        ))}
                    </StyledOptionsDiv>

                    {validationError.isInvalid && (<ErrorMessageViewer errMsg={validationError.message} />)}

                    <ActionButtonsForColumnEditors
                        handleSave={handleSave}
                        handleCancel={() => handleClose(getCheckedValuesInOrder(), initialValue, onCancel, validationError, setValidationError)}
                        isSaveDisabled={isSaving}
                        isCloseDisabled={isSaving}
                    />

                    {isSaving && <SavingSpinnerForColumnEditors />}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CheckboxGroupReusableEditor;

const StyledOptionsDiv = styled.div`
    max-height: 200px;
    overflow-y: auto;
`;

const StyledListItem = styled(ListItem)`
    height: auto;
    padding: 3px 0px 3px 14px;
`;
